import { useEffect, useRef, useState, useMemo, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Badge } from "@/components/ui/badge";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Scissors, RotateCcw, Save, Loader2, MapPin, AlertTriangle, HelpCircle, Check, ScissorsLineDashed } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

type TrimMode = 'keep' | 'clip';

interface TrackPoint {
  seq: number;
  lon: number;
  lat: number;
  elevation?: number;
  recorded_at?: string;
  distance_miles?: number;
}

interface TrackEditorProps {
  runId: string;
  onSave?: () => void;
  onCancel?: () => void;
}

const TrackEditor = ({ runId, onSave, onCancel }: TrackEditorProps) => {
  const { toast } = useToast();
  const mapRef = useRef<HTMLDivElement>(null);
  const viewRef = useRef<__esri.MapView | null>(null);
  const [trackPoints, setTrackPoints] = useState<TrackPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [trimRange, setTrimRange] = useState<[number, number]>([0, 100]);
  const [excludedPoints, setExcludedPoints] = useState<Set<number>>(new Set());
  const [trimMode, setTrimMode] = useState<TrimMode>('keep');
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  // Detect if the slider has been modified from default position
  const hasModifiedRange = trimRange[0] !== 0 || trimRange[1] !== 100;

  // Calculate trimmed indices based on percentage
  const trimmedIndices = useMemo(() => {
    if (trackPoints.length === 0) return { start: 0, end: 0 };
    const start = Math.round((trimRange[0] / 100) * (trackPoints.length - 1));
    const end = Math.round((trimRange[1] / 100) * (trackPoints.length - 1));
    return { start, end };
  }, [trimRange, trackPoints.length]);

  // Calculate stats based on trim mode
  const stats = useMemo(() => {
    const originalCount = trackPoints.length;
    const { start, end } = trimmedIndices;
    const selectedCount = end - start + 1;
    
    // Count excluded points that are within the relevant range
    const excludedInSelection = Array.from(excludedPoints).filter(
      seq => seq >= start && seq <= end
    ).length;
    const excludedOutsideSelection = Array.from(excludedPoints).filter(
      seq => seq < start || seq > end
    ).length;

    let finalCount: number;
    let removedCount: number;

    if (trimMode === 'keep') {
      // Keep mode: keep selected range, remove outside
      finalCount = selectedCount - excludedInSelection;
      removedCount = originalCount - finalCount;
    } else {
      // Clip mode: remove selected range, keep outside
      const outsideCount = originalCount - selectedCount;
      finalCount = outsideCount - excludedOutsideSelection;
      removedCount = originalCount - finalCount;
    }
    
    return {
      originalCount,
      selectedCount,
      excludedCount: excludedInSelection + excludedOutsideSelection,
      finalCount,
      removedCount,
      trimMode,
    };
  }, [trackPoints.length, trimmedIndices, excludedPoints, trimMode]);

  // Fetch track points with pagination to bypass 1000 row limit
  const fetchTracks = useCallback(async () => {
    try {
      setLoading(true);
      const allPoints: TrackPoint[] = [];
      let start = 0;
      const batchSize = 1000;
      let hasMore = true;

      while (hasMore) {
        const { data, error } = await supabase
          .from('run_tracks')
          .select('seq, geom, elevation, recorded_at, distance_miles')
          .eq('run_id', runId)
          .order('seq')
          .range(start, start + batchSize - 1);

        if (error) throw error;

        if (data && data.length > 0) {
          data.forEach(track => {
            const geom = track.geom as any;
            allPoints.push({
              seq: track.seq,
              lon: geom.coordinates[0],
              lat: geom.coordinates[1],
              elevation: track.elevation ?? undefined,
              recorded_at: track.recorded_at ?? undefined,
              distance_miles: track.distance_miles ?? undefined,
            });
          });
          start += batchSize;
          hasMore = data.length === batchSize;
        } else {
          hasMore = false;
        }
      }

      setTrackPoints(allPoints);
      setTrimRange([0, 100]);
      setExcludedPoints(new Set());
      setTrimMode('keep');
    } catch (error) {
      console.error('Error fetching track points:', error);
      toast({
        title: "Error loading track",
        description: "Failed to load track data for editing.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [runId, toast]);

  // Initial fetch
  useEffect(() => {
    fetchTracks();
  }, [fetchTracks]);

  // Initialize map
  useEffect(() => {
    if (!mapRef.current || trackPoints.length === 0) return;

    let view: __esri.MapView;
    let mounted = true;

    const initMap = async () => {
      const [Map, MapView, GraphicsLayer, Graphic, Polyline, SimpleLineSymbol, SimpleMarkerSymbol] = await Promise.all([
        import("@arcgis/core/Map").then(m => m.default),
        import("@arcgis/core/views/MapView").then(m => m.default),
        import("@arcgis/core/layers/GraphicsLayer").then(m => m.default),
        import("@arcgis/core/Graphic").then(m => m.default),
        import("@arcgis/core/geometry/Polyline").then(m => m.default),
        import("@arcgis/core/symbols/SimpleLineSymbol").then(m => m.default),
        import("@arcgis/core/symbols/SimpleMarkerSymbol").then(m => m.default),
      ]);

      if (!mounted || !mapRef.current) return;

      const trackLayer = new GraphicsLayer({ id: 'track-layer' });
      const trimmedLayer = new GraphicsLayer({ id: 'trimmed-layer' });
      const pointsLayer = new GraphicsLayer({ id: 'points-layer' });

      const map = new Map({
        basemap: "topo-vector",
        layers: [trackLayer, trimmedLayer, pointsLayer],
      });

      // Calculate center from track points
      const centerLon = trackPoints.reduce((sum, p) => sum + p.lon, 0) / trackPoints.length;
      const centerLat = trackPoints.reduce((sum, p) => sum + p.lat, 0) / trackPoints.length;

      view = new MapView({
        container: mapRef.current,
        map,
        center: [centerLon, centerLat],
        zoom: 12,
        ui: { components: ["zoom"] },
      });

      viewRef.current = view;

      // Draw original track (faded)
      const allCoords = trackPoints.map(p => [p.lon, p.lat]);
      const originalLine = new Polyline({
        paths: [allCoords],
        spatialReference: { wkid: 4326 },
      });

      trackLayer.add(new Graphic({
        geometry: originalLine,
        symbol: new SimpleLineSymbol({
          color: [128, 128, 128, 0.3],
          width: 3,
        }),
      }));

      // Zoom to track
      await view.goTo(originalLine.extent.expand(1.2));

      // Update trimmed display
      const updateTrimmedDisplay = () => {
        trimmedLayer.removeAll();
        pointsLayer.removeAll();

        const { start, end } = trimmedIndices;
        const trimmedCoords = trackPoints
          .slice(start, end + 1)
          .filter(p => !excludedPoints.has(p.seq))
          .map(p => [p.lon, p.lat]);

        if (trimmedCoords.length > 1) {
          const trimmedLine = new Polyline({
            paths: [trimmedCoords],
            spatialReference: { wkid: 4326 },
          });

          trimmedLayer.add(new Graphic({
            geometry: trimmedLine,
            symbol: new SimpleLineSymbol({
              color: [34, 197, 94, 1],
              width: 4,
            }),
          }));
        }

        // Show start/end markers
        if (trimmedCoords.length > 0) {
          const startPoint = trimmedCoords[0];
          const endPoint = trimmedCoords[trimmedCoords.length - 1];

          pointsLayer.add(new Graphic({
            geometry: { type: "point", longitude: startPoint[0], latitude: startPoint[1] } as any,
            symbol: new SimpleMarkerSymbol({
              color: [34, 197, 94, 1],
              outline: { color: [255, 255, 255], width: 2 },
              size: 12,
            }),
          }));

          pointsLayer.add(new Graphic({
            geometry: { type: "point", longitude: endPoint[0], latitude: endPoint[1] } as any,
            symbol: new SimpleMarkerSymbol({
              color: [239, 68, 68, 1],
              outline: { color: [255, 255, 255], width: 2 },
              size: 12,
            }),
          }));
        }
      };

      updateTrimmedDisplay();
    };

    initMap();

    return () => {
      mounted = false;
      if (view) {
        view.destroy();
      }
    };
  }, [trackPoints]);

  // Update map when trim range, excluded points, or trim mode change
  useEffect(() => {
    if (!viewRef.current || trackPoints.length === 0) return;

    const updateDisplay = async () => {
      const view = viewRef.current!;
      const trimmedLayer = view.map.findLayerById('trimmed-layer') as __esri.GraphicsLayer;
      const pointsLayer = view.map.findLayerById('points-layer') as __esri.GraphicsLayer;

      if (!trimmedLayer || !pointsLayer) return;

      const [Graphic, Polyline, SimpleLineSymbol, SimpleMarkerSymbol] = await Promise.all([
        import("@arcgis/core/Graphic").then(m => m.default),
        import("@arcgis/core/geometry/Polyline").then(m => m.default),
        import("@arcgis/core/symbols/SimpleLineSymbol").then(m => m.default),
        import("@arcgis/core/symbols/SimpleMarkerSymbol").then(m => m.default),
      ]);

      trimmedLayer.removeAll();
      pointsLayer.removeAll();

      const { start, end } = trimmedIndices;

      if (trimMode === 'keep') {
        // Keep mode: show selected range in green (being kept)
        const keptCoords = trackPoints
          .slice(start, end + 1)
          .filter(p => !excludedPoints.has(p.seq))
          .map(p => [p.lon, p.lat]);

        if (keptCoords.length > 1) {
          const keptLine = new Polyline({
            paths: [keptCoords],
            spatialReference: { wkid: 4326 },
          });

          trimmedLayer.add(new Graphic({
            geometry: keptLine,
            symbol: new SimpleLineSymbol({
              color: [34, 197, 94, 1], // Green - keeping
              width: 4,
            }),
          }));
        }

        // Show start/end markers for kept portion
        if (keptCoords.length > 0) {
          const startPoint = keptCoords[0];
          const endPoint = keptCoords[keptCoords.length - 1];

          pointsLayer.add(new Graphic({
            geometry: { type: "point", longitude: startPoint[0], latitude: startPoint[1] } as any,
            symbol: new SimpleMarkerSymbol({
              color: [34, 197, 94, 1],
              outline: { color: [255, 255, 255], width: 2 },
              size: 12,
            }),
          }));

          pointsLayer.add(new Graphic({
            geometry: { type: "point", longitude: endPoint[0], latitude: endPoint[1] } as any,
            symbol: new SimpleMarkerSymbol({
              color: [34, 197, 94, 1],
              outline: { color: [255, 255, 255], width: 2 },
              size: 12,
            }),
          }));
        }
      } else {
        // Clip mode: show selected range in red (being removed), outside in green (being kept)
        const clippedCoords = trackPoints
          .slice(start, end + 1)
          .map(p => [p.lon, p.lat]);

        // Draw clipped portion in red
        if (clippedCoords.length > 1) {
          const clippedLine = new Polyline({
            paths: [clippedCoords],
            spatialReference: { wkid: 4326 },
          });

          trimmedLayer.add(new Graphic({
            geometry: clippedLine,
            symbol: new SimpleLineSymbol({
              color: [239, 68, 68, 0.8], // Red - removing
              width: 4,
            }),
          }));
        }

        // Draw kept portions (before and after selection) in green
        const beforeCoords = trackPoints
          .slice(0, start)
          .filter(p => !excludedPoints.has(p.seq))
          .map(p => [p.lon, p.lat]);
        
        const afterCoords = trackPoints
          .slice(end + 1)
          .filter(p => !excludedPoints.has(p.seq))
          .map(p => [p.lon, p.lat]);

        if (beforeCoords.length > 1) {
          trimmedLayer.add(new Graphic({
            geometry: new Polyline({
              paths: [beforeCoords],
              spatialReference: { wkid: 4326 },
            }),
            symbol: new SimpleLineSymbol({
              color: [34, 197, 94, 1], // Green - keeping
              width: 4,
            }),
          }));
        }

        if (afterCoords.length > 1) {
          trimmedLayer.add(new Graphic({
            geometry: new Polyline({
              paths: [afterCoords],
              spatialReference: { wkid: 4326 },
            }),
            symbol: new SimpleLineSymbol({
              color: [34, 197, 94, 1], // Green - keeping
              width: 4,
            }),
          }));
        }

        // Show clip markers at selection boundaries
        if (clippedCoords.length > 0) {
          const clipStart = clippedCoords[0];
          const clipEnd = clippedCoords[clippedCoords.length - 1];

          pointsLayer.add(new Graphic({
            geometry: { type: "point", longitude: clipStart[0], latitude: clipStart[1] } as any,
            symbol: new SimpleMarkerSymbol({
              color: [239, 68, 68, 1], // Red markers for clip boundaries
              outline: { color: [255, 255, 255], width: 2 },
              size: 12,
            }),
          }));

          pointsLayer.add(new Graphic({
            geometry: { type: "point", longitude: clipEnd[0], latitude: clipEnd[1] } as any,
            symbol: new SimpleMarkerSymbol({
              color: [239, 68, 68, 1],
              outline: { color: [255, 255, 255], width: 2 },
              size: 12,
            }),
          }));
        }
      }
    };

    updateDisplay();
  }, [trimRange, excludedPoints, trackPoints, trimmedIndices, trimMode]);

  const handleReset = () => {
    setTrimRange([0, 100]);
    setExcludedPoints(new Set());
    setTrimMode('keep');
  };

  const handleRemoveOutliers = async () => {
    if (trackPoints.length < 3) return;

    // Simple outlier detection based on distance from neighbors
    const newExcluded = new Set(excludedPoints);
    const { start, end } = trimmedIndices;

    for (let i = start + 1; i < end; i++) {
      const prev = trackPoints[i - 1];
      const curr = trackPoints[i];
      const next = trackPoints[i + 1];

      if (!prev || !next) continue;

      // Calculate distances
      const distToPrev = Math.sqrt(
        Math.pow(curr.lon - prev.lon, 2) + Math.pow(curr.lat - prev.lat, 2)
      );
      const distToNext = Math.sqrt(
        Math.pow(curr.lon - next.lon, 2) + Math.pow(curr.lat - next.lat, 2)
      );
      const directDist = Math.sqrt(
        Math.pow(next.lon - prev.lon, 2) + Math.pow(next.lat - prev.lat, 2)
      );

      // If point creates a significant detour, mark as outlier
      const detourRatio = (distToPrev + distToNext) / (directDist || 0.0001);
      if (detourRatio > 3) {
        newExcluded.add(curr.seq);
      }
    }

    setExcludedPoints(newExcluded);
    
    const removedCount = newExcluded.size - excludedPoints.size;
    if (removedCount > 0) {
      toast({
        title: "Outliers detected",
        description: `Marked ${removedCount} potential outlier point${removedCount > 1 ? 's' : ''} for removal.`,
      });
    } else {
      toast({
        title: "No outliers found",
        description: "The track looks clean!",
      });
    }
  };

  // Check if save requires confirmation
  const shouldConfirmSave = () => {
    const removalPercent = (stats.removedCount / stats.originalCount) * 100;
    return removalPercent > 25 || stats.finalCount < 10;
  };

  const handleSaveClick = () => {
    if (stats.removedCount === 0) {
      toast({
        title: "No changes",
        description: "No edits have been made to the track.",
      });
      return;
    }

    // Prevent saving if too few points remain
    if (stats.finalCount < 2) {
      toast({
        title: "Cannot save",
        description: "At least 2 points are required for a valid track.",
        variant: "destructive",
      });
      return;
    }

    if (shouldConfirmSave()) {
      setShowConfirmDialog(true);
    } else {
      executeSave();
    }
  };

  const executeSave = async () => {
    setSaving(true);
    try {
      const { start, end } = trimmedIndices;
      const excludedArray = Array.from(excludedPoints);

      let startSeq: number;
      let endSeq: number;

      if (trimMode === 'keep') {
        // Keep mode: keep the selected range
        startSeq = trackPoints[start]?.seq || 0;
        endSeq = trackPoints[end]?.seq || trackPoints.length - 1;
      } else {
        // Clip mode: remove the selected range, keep everything outside
        // We need to exclude all points in the selected range
        const clippedSequences = trackPoints
          .slice(start, end + 1)
          .map(p => p.seq);
        
        // Add clipped sequences to excluded array
        const allExcluded = [...excludedArray, ...clippedSequences];
        
        const { error } = await supabase.rpc('save_edited_tracks' as any, {
          p_run_id: runId,
          p_start_seq: trackPoints[0]?.seq || 0,
          p_end_seq: trackPoints[trackPoints.length - 1]?.seq || 0,
          p_excluded_sequences: allExcluded,
        });

        if (error) throw error;

        toast({
          title: "Track saved",
          description: `Clipped ${stats.removedCount} points from your track.`,
        });

        // Refresh data after save
        await fetchTracks();
        onSave?.();
        setSaving(false);
        return;
      }

      const { error } = await supabase.rpc('save_edited_tracks' as any, {
        p_run_id: runId,
        p_start_seq: startSeq,
        p_end_seq: endSeq,
        p_excluded_sequences: excludedArray,
      });

      if (error) throw error;

      toast({
        title: "Track saved",
        description: `Removed ${stats.removedCount} points from your track.`,
      });

      // Refresh data after save
      await fetchTracks();
      onSave?.();
    } catch (error) {
      console.error('Error saving track edits:', error);
      toast({
        title: "Error saving",
        description: "Failed to save track edits. Please try again.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (trackPoints.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12 text-center">
          <MapPin className="h-12 w-12 text-muted-foreground mb-4" />
          <p className="text-muted-foreground">No track data available for editing.</p>
        </CardContent>
      </Card>
    );
  }

  const removalPercent = Math.round((stats.removedCount / stats.originalCount) * 100);

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Scissors className="h-5 w-5" />
            Track Editor
          </CardTitle>
          <CardDescription>
            Trim the start and end of your track, or remove GPS errors
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Map Preview */}
          <div 
            ref={mapRef} 
            className="w-full h-64 rounded-lg overflow-hidden border border-border"
          />

          {/* Stats */}
          <div className="flex flex-wrap gap-2">
            <Badge variant="outline" className="text-xs">
              Original: {stats.originalCount} points
            </Badge>
            <Badge variant="secondary" className="text-xs">
              Keeping: {stats.finalCount} points
            </Badge>
            {stats.removedCount > 0 && (
              <Badge variant="destructive" className="text-xs">
                Removing: {stats.removedCount} points
              </Badge>
            )}
          </div>

          {/* Trim Slider */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium">Select Range</label>
              <span className="text-xs text-muted-foreground">
                {trimRange[0]}% - {trimRange[1]}%
              </span>
            </div>
            <Slider
              value={trimRange}
              onValueChange={(value) => setTrimRange(value as [number, number])}
              min={0}
              max={100}
              step={1}
              className="w-full"
            />
            
            {/* Keep/Clip Toggle - appears when slider is modified */}
            {hasModifiedRange && (
              <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50 border border-border">
                <span className="text-sm text-muted-foreground">Selection action:</span>
                <ToggleGroup
                  type="single"
                  value={trimMode}
                  onValueChange={(value) => value && setTrimMode(value as TrimMode)}
                  className="gap-1"
                >
                  <ToggleGroupItem
                    value="keep"
                    aria-label="Keep selection"
                    className="gap-1.5 data-[state=on]:bg-green-500/20 data-[state=on]:text-green-600 data-[state=on]:border-green-500/50"
                  >
                    <Check className="h-4 w-4" />
                    Keep
                  </ToggleGroupItem>
                  <ToggleGroupItem
                    value="clip"
                    aria-label="Clip selection"
                    className="gap-1.5 data-[state=on]:bg-red-500/20 data-[state=on]:text-red-600 data-[state=on]:border-red-500/50"
                  >
                    <ScissorsLineDashed className="h-4 w-4" />
                    Clip
                  </ToggleGroupItem>
                </ToggleGroup>
                <HoverCard>
                  <HoverCardTrigger asChild>
                    <HelpCircle className="h-4 w-4 text-muted-foreground cursor-help" />
                  </HoverCardTrigger>
                  <HoverCardContent className="w-72">
                    <div className="space-y-2 text-sm">
                      <p><strong className="text-green-600">Keep:</strong> Keep only the selected portion, remove everything outside.</p>
                      <p><strong className="text-red-600">Clip:</strong> Remove the selected portion, keep everything outside.</p>
                    </div>
                  </HoverCardContent>
                </HoverCard>
              </div>
            )}

            <div className="flex justify-between text-xs text-muted-foreground">
              <span className="flex items-center gap-1">
                <div className={`w-2 h-2 rounded-full ${trimMode === 'keep' ? 'bg-green-500' : 'bg-red-500'}`} />
                {trimMode === 'keep' ? 'Keep Start' : 'Clip Start'}
              </span>
              <span className="flex items-center gap-1">
                <div className={`w-2 h-2 rounded-full ${trimMode === 'keep' ? 'bg-green-500' : 'bg-red-500'}`} />
                {trimMode === 'keep' ? 'Keep End' : 'Clip End'}
              </span>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-2">
            <HoverCard>
              <HoverCardTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRemoveOutliers}
                  disabled={saving}
                >
                  <AlertTriangle className="h-4 w-4 mr-2" />
                  Detect GPS Errors
                  <HelpCircle className="h-3 w-3 ml-1 text-muted-foreground" />
                </Button>
              </HoverCardTrigger>
              <HoverCardContent className="w-80">
                <p className="text-sm text-muted-foreground">
                  Automatically finds points that appear to be GPS errors — sudden jumps 
                  that create unrealistic detours in your track. Detected points will be 
                  marked for removal but won't be deleted until you save.
                </p>
              </HoverCardContent>
            </HoverCard>
            <Button
              variant="outline"
              size="sm"
              onClick={handleReset}
              disabled={saving}
            >
              <RotateCcw className="h-4 w-4 mr-2" />
              Reset
            </Button>
          </div>

          {/* Save/Cancel */}
          <div className="flex justify-end gap-2 pt-4 border-t border-border">
            {onCancel && (
              <Button variant="outline" onClick={onCancel} disabled={saving}>
                Cancel
              </Button>
            )}
            <Button onClick={handleSaveClick} disabled={saving || stats.removedCount === 0}>
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Confirmation Dialog */}
      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Confirm Track Edit
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p>You are about to permanently remove a significant portion of your track:</p>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="p-2 rounded bg-muted">
                    <span className="text-muted-foreground">Original:</span>
                    <span className="ml-2 font-medium">{stats.originalCount} points</span>
                  </div>
                  <div className="p-2 rounded bg-destructive/10 text-destructive">
                    <span>Removing:</span>
                    <span className="ml-2 font-medium">{stats.removedCount} points ({removalPercent}%)</span>
                  </div>
                  <div className="p-2 rounded bg-muted col-span-2">
                    <span className="text-muted-foreground">Final track:</span>
                    <span className="ml-2 font-medium">{stats.finalCount} points</span>
                  </div>
                </div>
                {removalPercent > 50 && (
                  <p className="text-amber-600 font-medium">
                    ⚠️ This will remove more than half of your track.
                  </p>
                )}
                <p className="text-muted-foreground text-sm">
                  This action cannot be undone. The removed points will be permanently deleted.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={executeSave}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Confirm & Save
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default TrackEditor;
